import { useEffect, useState } from 'react';
import { BookingScreen } from '../liveBooking/BookingScreen';
import { sendLiveTimeCard, type LessonMode } from '../../services/liveV2';
import { getLiveBookingContext, liveLessonsErrorMessage } from '../../services/liveLessons';
import { useToast } from '../../hooks/useToast';
import { useUserIdentity } from '../../hooks/useUserIdentity';
import { useViewerZone } from '../../hooks/useViewerZone';
import { bookingWindowFrom } from '../../utils/bookingWindow';
import { firstNameOf } from '../../utils/liveLessonText';
import type { CoachAvailabilitySlot, LiveMembership } from '../../types/liveCall';
import Sheet from '../Sheet';

/**
 * Suggest a Time (the coach) and Request a Time (the member), from a
 * Monthly Coaching thread (redesign spec, batch 4): the one booking screen
 * with its header and button changed. The member picks Match Review or
 * Guidance Call on it, with their usual time first; the coach's card
 * leaves the mode to the member, who picks it on the card. Send puts a
 * card in the thread (sendLiveTimeCard) that the other side books with one
 * tap.
 */
export function TimeCardComposer({
  membership, role, otherId, otherName, onClose,
}: {
  membership: LiveMembership;
  role: 'coach' | 'player';
  otherId: string;
  otherName: string;
  onClose: () => void;
}) {
  const { showError } = useToast();
  const tz = useViewerZone();
  const [zone, setZone] = useState(tz);
  const [mode, setMode] = useState<LessonMode>('match_review');
  const [slot, setSlot] = useState<CoachAvailabilitySlot | null>(null);
  const [sending, setSending] = useState(false);
  const [weekly, setWeekly] = useState<{ weekday: number; time: string } | null>(null);
  const other = firstNameOf(otherName, role === 'coach' ? 'your player' : 'your coach');
  const otherIdentity = useUserIdentity(otherId, role === 'coach' ? 'player' : 'coach');
  const isCoach = role === 'coach';

  // The member's weekly time, for "Your usual time".
  useEffect(() => {
    if (isCoach) return;
    let live = true;
    getLiveBookingContext(membership.coachId)
      .then((c) => { const w = c.membership?.weeklyTime; if (live && w) setWeekly({ weekday: w.weekday, time: w.time }); })
      .catch(() => { /* no usual time */ });
    return () => { live = false; };
  }, [isCoach, membership.coachId]);

  const send = async () => {
    if (!slot) return;
    setSending(true);
    try {
      await sendLiveTimeCard({ membershipId: membership.id, slotId: slot.id, ...(!isCoach && { lessonMode: mode }) });
      onClose();
    } catch (err) {
      showError(liveLessonsErrorMessage(err, 'Could not send the time. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  // The sheet (components.md 10): Cancel and the title in its bar, the booking calendar inside.
  return (
    <Sheet title={isCoach ? 'Suggest a Time' : 'Request a Time'} onClose={onClose} busy={sending}>
      <BookingScreen
        coachId={membership.coachId}
        offeringType="live_membership"
        coachName={otherIdentity?.name ?? otherName}
        coachFirstName={other}
        photoUrl={otherIdentity?.avatarUrl}
        title={isCoach ? 'Suggest a Time' : 'Request a Time'}
        line={isCoach ? `Pick one of your open times. ${other} can book it with one tap.`
          : `Pick an open time. ${other} can accept it with one tap.`}
        latestStartAt={bookingWindowFrom(new Date()).latestStartAt}
        timeZone={zone}
        onZoneChange={setZone}
        weekly={weekly}
        {...(!isCoach && { mode, onMode: setMode })}
        selected={slot}
        onSelect={setSlot}
        // The submit names the act (113's rule), never the person (audit 2 Nov, A15).
        buttonLabel={isCoach ? 'Suggest Time' : 'Request Time'}
        busy={sending}
        onSubmit={() => { void send(); }}
        onBack={onClose}
        // In the thread already: with no open times, Message {coach} goes back to it.
        onMessage={async () => onClose()}
        ownTimes={isCoach}
        inSheet
      />
    </Sheet>
  );
}
